import { useCallback, useEffect, useRef, useState } from 'react';

import { toPersianDigits } from '@bookora/shared';
import { Modal, Numeric, Progress, Typography } from '@bookora/ui';
import { useNavigate } from 'react-router-dom';

import { logout } from '@/api/endpoints/auth';
import { env } from '@/lib/env';

import { useSessionStore } from './token-store';

/**
 * Idle auto-logout.
 *
 * ── WHY A CLINIC PANEL NEEDS THIS MORE THAN MOST APPS ───────────────────────
 *
 * This panel sits open on a reception desk in a room patients walk through, and
 * it displays other patients' names, phone numbers, clinical notes and
 * before/after photographs. An unattended session is a disclosure, not an
 * inconvenience. SPEC §5 lists a configurable idle timeout alongside the device
 * list and remote revoke for exactly that reason.
 *
 * ── BEHAVIOUR ───────────────────────────────────────────────────────────────
 *
 * After `VITE_SESSION_IDLE_MINUTES` with no pointer, key, scroll or touch, a
 * modal appears and counts down `env.idleGraceSeconds`. Answering it resets the
 * clock; letting it run out revokes the refresh token server-side and returns
 * to the login screen with `?reason=idle`, so the user is told why.
 *
 * ── DETAILS THAT MATTER ─────────────────────────────────────────────────────
 *
 *  - Activity is tracked with a ref and a single interval, not a state update
 *    per mousemove. Re-rendering the shell on pointer movement would make the
 *    whole panel feel heavy.
 *  - `localStorage` carries the last-activity stamp across TABS: a doctor
 *    working in a second tab is not idle, and would otherwise be logged out of
 *    the tab they are actively using.
 *  - The warning is a modal rather than a toast on purpose. It has to be
 *    impossible to miss, and it has to be dismissible only by a deliberate act.
 */

const ACTIVITY_KEY = 'bookora_clinic_last_activity';
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'focus'] as const;
/** How often the clock is checked. One second keeps the countdown honest. */
const TICK_MS = 1_000;
/** Cross-tab writes are throttled; the stamp does not need per-event precision. */
const WRITE_THROTTLE_MS = 5_000;

function readLastActivity(): number {
  try {
    const raw = localStorage.getItem(ACTIVITY_KEY);
    const parsed = raw === null ? Number.NaN : Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : Date.now();
  } catch {
    return Date.now();
  }
}

function writeLastActivity(at: number): void {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(at));
  } catch {
    // Quota or private mode. Idle tracking degrades to per-tab, which is the
    // safe direction — it logs out sooner, never later.
  }
}

export function IdleTimeout() {
  const navigate = useNavigate();
  const accessToken = useSessionStore((state) => state.accessToken);
  const refreshToken = useSessionStore((state) => state.refreshToken);
  const clearSession = useSessionStore((state) => state.clearSession);

  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number>(env.idleGraceSeconds);

  const lastActivityRef = useRef(Date.now());
  const lastWriteRef = useRef(0);
  const signingOutRef = useRef(false);

  const idleMs = env.idleMinutes * 60_000;
  const graceMs = env.idleGraceSeconds * 1_000;

  const markActive = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    if (now - lastWriteRef.current > WRITE_THROTTLE_MS) {
      lastWriteRef.current = now;
      writeLastActivity(now);
    }
  }, []);

  const signOut = useCallback(async () => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;

    // Best effort: revoking the refresh token server-side is what makes this a
    // real logout rather than a screen lock. A failure here still clears the
    // client, because leaving the tokens in localStorage would be worse.
    try {
      if (refreshToken) await logout(refreshToken);
    } catch {
      // ignored — see above
    }

    clearSession();
    void navigate('/login?reason=idle', { replace: true });
  }, [refreshToken, clearSession, navigate]);

  /* -- activity listeners --------------------------------------------------- */

  useEffect(() => {
    if (!accessToken) return;

    markActive();
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, markActive, { passive: true });
    }

    function handleStorage(event: StorageEvent): void {
      if (event.key !== ACTIVITY_KEY || event.newValue === null) return;
      const stamp = Number.parseInt(event.newValue, 10);
      if (Number.isFinite(stamp) && stamp > lastActivityRef.current) {
        lastActivityRef.current = stamp;
        setWarning(false);
      }
    }
    window.addEventListener('storage', handleStorage);

    return () => {
      for (const event of ACTIVITY_EVENTS) window.removeEventListener(event, markActive);
      window.removeEventListener('storage', handleStorage);
    };
  }, [accessToken, markActive]);

  /* -- the clock ------------------------------------------------------------ */

  useEffect(() => {
    if (!accessToken) {
      setWarning(false);
      signingOutRef.current = false;
      return;
    }

    const id = window.setInterval(() => {
      const last = Math.max(lastActivityRef.current, readLastActivity());
      const idleFor = Date.now() - last;

      if (idleFor >= idleMs + graceMs) {
        void signOut();
        return;
      }

      if (idleFor >= idleMs) {
        setWarning(true);
        setSecondsLeft(Math.max(0, Math.ceil((idleMs + graceMs - idleFor) / 1000)));
      } else {
        setWarning(false);
      }
    }, TICK_MS);

    return () => window.clearInterval(id);
  }, [accessToken, idleMs, graceMs, signOut]);

  if (!accessToken) return null;

  return (
    <Modal
      open={warning}
      centered
      closable={false}
      mask={{ closable: false }}
      keyboard={false}
      title="به‌زودی از حساب خارج می‌شید"
      okText="ادامهٔ کار"
      cancelText="خروج از حساب"
      onOk={() => {
        markActive();
        setWarning(false);
      }}
      onCancel={() => void signOut()}
      okButtonProps={{ autoFocus: true }}
    >
      <Typography.Paragraph>
        چون مدتی فعالیتی نداشتید، تا <Numeric as="strong">{toPersianDigits(secondsLeft)}</Numeric>{' '}
        ثانیهٔ دیگه از حساب خارج می‌شید.
      </Typography.Paragraph>
      <Progress
        percent={Math.round((secondsLeft / env.idleGraceSeconds) * 100)}
        showInfo={false}
        // `normal`, not `active`: AntD's active bar runs a looping light sweep,
        // and the only motion this bar needs is the countdown itself.
        status={secondsLeft <= 10 ? 'exception' : 'normal'}
        aria-label="زمان باقی‌مانده تا خروج خودکار"
      />
    </Modal>
  );
}
